/**
 * Client-side prediction + server reconciliation for the LOCAL ship only.
 * We step the exact same `stepShipBody` the server runs, so the local ship
 * responds instantly; when an authoritative snapshot arrives we snap to it and
 * replay the inputs the server hadn't yet processed. Same code => no drift.
 */
import { INPUT_HZ } from '@piratas/protocol';
import { collideCircles, enforceWorldEdge, stepShipBody, type Obstacle, type ShipBody, type ShipInput, type Wind } from '@piratas/sim';
import type { SnapshotShip } from '@piratas/protocol';

const INPUT_DT = 1 / INPUT_HZ;

export class Prediction {
  body: ShipBody = { x: 0, y: 0, heading: 0, speed: 0, angVel: 0, sail: 0, knockX: 0, knockY: 0, cargoLoad: 0 };
  hasBaseline = false;
  /** Set true on the ticks the map-border turnaround is steering the LOCAL ship (from the shared, deterministic
   *  `enforceWorldEdge`, so it matches the server). main.ts flashes the "Flat Earth" banner and clears it. */
  hitEdge = false;
  private pending: { seq: number; input: ShipInput }[] = [];
  // Solid base islands + this ship's collision radius — the SAME set the server
  // resolves, applied right after each stepShipBody so prediction can't tunnel.
  private obstacles: readonly Obstacle[] = [];
  private shipR = 0;

  /** Update the solid obstacles (base islands) + local ship radius. Cheap; call on base/class change. */
  setObstacles(obstacles: readonly Obstacle[], shipR: number): void {
    this.obstacles = obstacles;
    this.shipR = shipR;
  }

  /** Apply one fresh local input step (called at INPUT_HZ) and remember it. */
  applyInput(seq: number, input: ShipInput, wind: Wind, speedMul = 1, turnMul = 1): void {
    if (!this.hasBaseline) return;
    stepShipBody(this.body, input, wind, INPUT_DT, speedMul, turnMul);
    collideCircles(this.body, this.obstacles, this.shipR);
    if (enforceWorldEdge(this.body, INPUT_DT)) this.hitEdge = true; // rising edge → banner (matches server truth)
    this.pending.push({ seq, input: { ...input } });
    if (this.pending.length > 256) this.pending.shift();
  }

  /** Reconcile against the authoritative self-ship from the latest snapshot. `knockX/knockY` is the
   *  server's CURRENT (decayed) ram-shove velocity from SelfState — seeding it here lets the replay decay
   *  the shove on top of the authoritative post-knock position, so the local ship predicts the ram exactly. */
  reconcile(auth: SnapshotShip, ackSeq: number, wind: Wind, speedMul = 1, turnMul = 1, knockX = 0, knockY = 0, cargoLoad = 0): void {
    // Snap kinematic state to authority (angVel persists — it's continuous and
    // re-derived by the replay below).
    this.body.x = auth.x;
    this.body.y = auth.y;
    this.body.heading = auth.heading;
    this.body.speed = auth.speed;
    this.body.sail = auth.sail;
    this.body.knockX = knockX;
    this.body.knockY = knockY;
    // Cargo Runs: the authoritative weight load (from SelfState, not the transform snapshot). Set BEFORE the
    // replay so the replayed stepShipBody scrubs top speed exactly like the server did — no laden rubber-band.
    this.body.cargoLoad = cargoLoad;

    // Drop acknowledged inputs, replay the rest on top of the fresh baseline.
    this.pending = this.pending.filter((p) => p.seq > ackSeq);
    for (const p of this.pending) {
      stepShipBody(this.body, p.input, wind, INPUT_DT, speedMul, turnMul);
      collideCircles(this.body, this.obstacles, this.shipR);
      enforceWorldEdge(this.body, INPUT_DT); // keep the replay == server; banner only fires from the live applyInput
    }

    this.hasBaseline = true;
  }

  /** Initialize the predicted body directly from an authoritative state. */
  initFrom(auth: SnapshotShip, cargoLoad = 0): void {
    this.body.x = auth.x; this.body.y = auth.y; this.body.heading = auth.heading;
    this.body.speed = auth.speed; this.body.angVel = 0; this.body.sail = auth.sail;
    this.body.knockX = 0; this.body.knockY = 0; this.body.cargoLoad = cargoLoad;
    this.pending.length = 0;
    this.hasBaseline = true;
  }
}
